import { useState } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { Pizza, ArrowRight, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import api from "../lib/api";

export default function LoginPage() {
  const { login, loading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    try {
      const data = await login(email.trim(), password);
      if (data?.requires_2fa) {
        navigate("/login/verify-2fa", { state: { challengeToken: data.challenge_token, email: email.trim() } });
        return;
      }
      const u = data.user;
      toast.success(t("login.welcomeToast", { name: u.full_name }));
      if (u.must_change_password) {
        navigate("/cambiar-password");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || t("common.errorConnection");
      setError(msg);
      toast.error(msg);
    }
  };

  const submitForgot = async (e) => {
    e?.preventDefault();
    setForgotBusy(true);
    try {
      await api.post("/auth/request-password-reset", { email: forgotEmail.trim() });
      toast.success(t("forgotPassword.sent"));
      setForgotOpen(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      {/* Language switcher always visible top-right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher variant="login" />
      </div>

      {/* Left: Hero */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative overflow-hidden bg-[#1A1A1A] text-white p-12">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #FFDF00 0%, transparent 30%), radial-gradient(circle at 80% 70%, #FFDF00 0%, transparent 25%)",
          }}
        />
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-[#FFDF00]/10 blur-3xl" />
        <div className="absolute top-1/2 left-1/4 w-80 h-80 rounded-full bg-[#FFDF00]/5 blur-3xl" />

        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#FFDF00] flex items-center justify-center">
              <Pizza className="w-7 h-7 text-[#1A1A1A]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-extrabold text-2xl">Chizy Chiz</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#FFDF00] font-semibold">
                {t("nav.procurement")}
              </div>
            </div>
          </motion.div>
        </div>

        <div className="relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-display font-extrabold text-5xl xl:text-6xl leading-[0.95] tracking-tight"
          >
            {t("login.heroLineA")}
            <br />
            <span className="text-[#FFDF00]">{t("login.heroLineB")}</span>
            <br />
            {t("login.heroLineC")}
          </motion.h1>
        </div>

        <div className="relative z-10 text-xs text-slate-500">
          {t("login.footer")}
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
              <Pizza className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.5} />
            </div>
            <div className="font-display font-extrabold text-xl">Chizy Chiz</div>
          </div>

          <h2 className="font-display font-extrabold text-3xl text-[#1A1A1A] tracking-tight">
            {t("login.title")}
          </h2>
          <p className="text-slate-500 mt-1.5 text-sm">
            {t("login.subtitle")}
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">{t("common.email")}</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="claudia@chizychiz.com"
                className="mt-1.5 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00] transition-all"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[#1A1A1A] uppercase tracking-wider">{t("login.password")}</label>
              <div className="relative mt-1.5">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00] transition-all"
                  required
                />
                <button
                  data-testid="login-toggle-password-btn"
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                  title={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#1A1A1A] transition-colors p-1"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading || !email || !password}
              className="w-full btn-primary-chizy py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t("login.logging") : (
                <>
                  {t("login.login")}
                  <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </>
              )}
            </button>
          </form>

          <div className="mt-3 text-center">
            <button
              data-testid="login-forgot-password-link"
              type="button"
              onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
              className="text-xs font-semibold text-slate-500 hover:text-[#1A1A1A] underline decoration-[#FFDF00] decoration-2 underline-offset-2"
            >
              {t("forgotPassword.link")}
            </button>
          </div>

          <div className="mt-6">
            <div className="pt-5 border-t border-slate-200 text-center">
              <p className="text-xs text-slate-500">
                {t("login.supplierQuestion")}{" "}
                <a
                  href="/registro-proveedor"
                  data-testid="login-register-supplier-link"
                  className="font-bold text-[#1A1A1A] underline decoration-[#FFDF00] decoration-2 underline-offset-2 hover:decoration-4"
                >
                  {t("login.registerHere")}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {forgotOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setForgotOpen(false)}>
          <div data-testid="forgot-password-modal" className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg text-[#1A1A1A]">{t("forgotPassword.title")}</h3>
            <p className="text-sm text-slate-500 mt-1">{t("forgotPassword.subtitle")}</p>
            <form onSubmit={submitForgot}>
              <input
                data-testid="forgot-email-input"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="tu@correo.com"
                className="mt-3 w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                required
                autoFocus
              />
              <div className="flex gap-2 mt-4">
                <button type="button" onClick={() => setForgotOpen(false)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold hover:bg-slate-50">
                  {t("common.cancel")}
                </button>
                <button
                  data-testid="forgot-submit-btn"
                  type="submit"
                  disabled={forgotBusy || !forgotEmail}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-[#1A1A1A] hover:bg-black disabled:opacity-50"
                >
                  {forgotBusy ? t("common.saving") : t("forgotPassword.send")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
